import { useEffect, useRef, useState } from "react";
import { BACKEND_URL } from "../lib/api";
import { logger } from "../lib/logger";
import { toast } from "sonner";

/**
 * Subscribes to /api/auctions/ws/{auctionId} for live bid updates.
 * - Calls `onSnapshot(auction)` and `onBid(msg)` for incoming events.
 * - Returns whether the WebSocket is currently connected.
 */
export function useAuctionWebSocket(auctionId, { onSnapshot, onBid } = {}) {
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);

  useEffect(() => {
    if (!auctionId) return;
    const wsProtocol = BACKEND_URL.startsWith("https") ? "wss" : "ws";
    const wsUrl = `${wsProtocol}://${BACKEND_URL.replace(/^https?:\/\//, "")}/api/auctions/ws/${auctionId}`;
    let pingInterval;
    let reconnectTimer;
    let closed = false;

    const connect = () => {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        setConnected(true);
        pingInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send("ping");
        }, 30000);
      };

      ws.onmessage = (evt) => {
        try {
          const msg = JSON.parse(evt.data);
          if (msg.type === "snapshot" && msg.auction) {
            onSnapshot?.(msg.auction);
          } else if (msg.type === "bid") {
            onBid?.(msg);
            toast.info(`New bid: $${msg.current_bid.toLocaleString()} by ${msg.current_bidder_name}`);
          }
        } catch (err) {
          // Malformed WS payload — log via logger, never crash the connection.
          logger.warn("Auction WS payload parse error:", err);
        }
      };

      ws.onclose = () => {
        setConnected(false);
        clearInterval(pingInterval);
        if (!closed) {
          reconnectTimer = setTimeout(connect, 15000);
        }
      };

      ws.onerror = () => {
        // onclose will fire; nothing to do here
      };
    };

    connect();
    return () => {
      closed = true;
      clearInterval(pingInterval);
      clearTimeout(reconnectTimer);
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.close();
      }
    };
  }, [auctionId, onSnapshot, onBid]);

  return { connected };
}
